import React, {FC, useState, useEffect, useMemo} from 'react';
import Box from '@material-ui/core/Box';
import {FilterIcon} from '@epic-ui/icons';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import {Widget} from '../../../../common/widget';
import {ContentSkeleton} from '../../../../common/widget/Widget';
import RadioOptionSelect from '../../../../radio-option-select/RadioOptionSelect';
import PlayrateChart from '../components/chart/PlayrateChart';
import PlayrateHighlights, {PLAYRATE_HIGHLIGHT_HIGHT} from './PlayrateHighlights';
import RadioGameSelect from '../../../../radio-game-select/RadioGameSelect';
import IconButton from '../../../../common/IconButton';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import useLocalization from '../../../../../hooks/useLocalization';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import usePlayrateData from './usePlayrateData';
import HeaderLabel from './HeaderLabel';
import {prepareLineChartExportData} from '../exportUtils';
import {axisPercentFormatter} from '../utils';
import useDurationFormatter from '../hooks/useDurationFormatter';

import {WidgetProps} from '../types';
import {Status, Playrate, Option} from '../../../../../types';

import {ChartWrapper, OptionWrapper, VersionSelectWrapper} from '../styles';

const PERCENTAGE_STEP = 5;
const HIGHLIGHT_TICKS = [25, 50, 75, 95];

const CODES = {
    title: 'epic.affiliate.fortnite_games.playrate.title',
    titleLabel: 'epic.affiliate.fortnite_games.playrate.title_label',
    tooltip: 'epic.affiliate.fortnite_games.playrate.title',
    version: 'epic.affiliate.fortnite_games.playrate.version.',
    xAxisLabel: 'epic.affiliate.fortnite_games.playrate.xaxis_label',
    yAxisLabel: 'epic.affiliate.fortnite_games.playrate.yaxis_label',
    versionPreffix: 'epic.affiliate.fortnite_games.version',
    noDataMessage: 'epic.affiliate.earnings.no_data'
};

const PlayrateWidget: FC<WidgetProps> = ({
    selectedGames,
    gamesList,
    keysMap,
    gamesLoading
}: WidgetProps) => {
    const getMessage = useLocalization();
    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const formatDuration = useDurationFormatter();

    const selectedGame = selectedGames[0];

    const [game, setGame] = useState(selectedGame);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const islandCode =
        (game && (game.value as string)) || (selectedGame && (selectedGame.value as string));

    const {data, versions, loading, error, reload} = usePlayrateData({
        islandCode
    });

    const currentVersion = versions[0];
    const versionOptions = versions.slice(1).map(value => ({
        label: `${getMessage(CODES.versionPreffix)}${value}`,
        value
    }));

    const [previousVersionOption, setPreviousVersionOption] = useState<Option>(versionOptions[0]);

    const previousVersion = previousVersionOption ? previousVersionOption.value : versions[1];

    const formattedData = data.map((stats, index) => {
        const rates = Object.values(stats);

        return {
            percentage: (index + 1) * PERCENTAGE_STEP,
            current: [...rates].pop(),
            previous: stats[previousVersion]
        };
    });

    const highlights = formattedData.filter(({percentage}) =>
        HIGHLIGHT_TICKS.includes(Number(percentage))
    );

    const selectedVersions = useMemo(() => {
        return Object.values(Playrate).map((value, index) => ({
            label: getMessage(`${CODES.version}${value}`, [
                `${getMessage(CODES.versionPreffix)}${versions[index]}`
            ]),
            value
        }));
    }, [currentVersion]);

    const isLoading = loading || gamesLoading;
    const message = formattedData.length
        ? getMessage(error) || error
        : getMessage(CODES.noDataMessage);
    const status = error || !formattedData.length ? Status.ERROR : undefined;

    // Handlers
    const handleGameChange = (_event, gameOption) => {
        setGame(gameOption);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Effects
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    const GameSelect = (
        <RadioGameSelect
            options={gamesList}
            value={game || selectedGame}
            onChange={handleGameChange}
        />
    );

    return (
        <PDFPictureTrap id={CODES.title} index={1}>
            <Widget
                height={675}
                header={getMessage(CODES.title)}
                headerAdornment={<HeaderLabel label={getMessage(CODES.titleLabel)} />}
                padding="15px"
                tooltip={getMessage(CODES.tooltip)}
                status={isLoading ? undefined : status}
                message={!isLoading && message}
                overflow="visible"
                retry={reload}
                headerContent={
                    isFmdBreakpoint && (
                        <Box mt={1} width="460px">
                            {GameSelect}
                        </Box>
                    )
                }
                actions={
                    <>
                        {!isFmdBreakpoint && (
                            <IconButton onClick={handleMobileMenuClick} isOpen={isMobileMenuOpen}>
                                <FilterIcon />
                            </IconButton>
                        )}
                        <WidgetDownloadButton
                            areaId={CODES.title}
                            fileName={getMessage(CODES.title)}
                            data={formattedData}
                            prepareCSVData={prepareLineChartExportData(keysMap)}
                            disabled={isLoading || !!error}
                        />
                    </>
                }
                options={
                    isMobileMenuOpen &&
                    !isFmdBreakpoint && <OptionWrapper>{GameSelect}</OptionWrapper>
                }>
                <ChartWrapper>
                    {isLoading && (
                        <ContentSkeleton
                            variant="rect"
                            width="100%"
                            height={PLAYRATE_HIGHLIGHT_HIGHT - 20}
                        />
                    )}
                    <PlayrateChart
                        height={600}
                        data={formattedData}
                        emptyDataKeys={[]}
                        selectedVersions={selectedVersions}
                        loading={isLoading}
                        xAxis="percentage"
                        xTickFormatter={axisPercentFormatter}
                        yTickFormatter={() => formatDuration}
                        xAxisProps={{
                            label: {
                                content: props => (
                                    <PlayrateHighlights {...props} data={highlights} />
                                )
                            }
                        }}
                        yAxisProps={{
                            label: {
                                value: getMessage(CODES.yAxisLabel),
                                angle: -90,
                                position: 'insideLeft',
                                offset: -5,
                                fill: theme.palette.dividerStrong,
                                fontSize: '12px'
                            }
                        }}>
                        {versionOptions.length && (
                            <VersionSelectWrapper>
                                <RadioOptionSelect
                                    options={versionOptions}
                                    value={
                                        previousVersionOption || {
                                            value: versions[1],
                                            label: `${getMessage(CODES.versionPreffix)}${
                                                versions[1]
                                            }`
                                        }
                                    }
                                    onChange={setPreviousVersionOption}
                                />
                            </VersionSelectWrapper>
                        )}
                    </PlayrateChart>
                </ChartWrapper>
            </Widget>
        </PDFPictureTrap>
    );
};

export default PlayrateWidget;
